import React, { useEffect, useRef, useState } from 'react';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * Curx AI marketing landing — Phase 5 rebuild.
 *
 * One scrollable page with anchor sections:
 *   #philosophy · #how-it-works · #features · #about · #waitlist
 *
 * Logged-out visitors at "/" land here; logged-in visitors are routed
 * to <Home /> by App.js. The old dark/gold inline LandingPage is
 * archived at src/Landing/_archive/LegacyLanding.old.js.
 */
export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.bg,
        color: colors.textPrimary,
        fontFamily: fontStack,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <SmoothScrollGlobal />
      <TopNav />

      <Hero onWatchDemo={() => setDemoOpen(true)} />
      <TrustSection />
      <Philosophy />
      <HowItWorks />
      <Features />
      <About />
      <FinalCTA />
      <Footer />

      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
    </div>
  );
}

/* ================================================================
   Top nav (sticky)
   ================================================================ */

function TopNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '#philosophy', label: 'Philosophy' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#features', label: 'Features' },
    { href: '#about', label: 'About' },
  ];

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: scrolled ? 'rgba(255,255,255,0.92)' : '#fff',
        backdropFilter: scrolled ? 'saturate(140%) blur(8px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'saturate(140%) blur(8px)' : 'none',
        borderBottom: `1px solid ${colors.border}`,
        transition: 'background-color 160ms ease',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <a
          href="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
            color: colors.textPrimary,
          }}
        >
          <CruxHexImg size={32} />
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: colors.textPrimary,
            }}
          >
            CURX AI
          </span>
        </a>

        <nav className="crux-nav-center" aria-label="Primary">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className="crux-nav-link">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="crux-nav-cta">
          <a href="/login" className="crux-btn crux-btn--ghost">Sign in</a>
          <a href="/login" className="crux-btn crux-btn--primary">Start Your Journey</a>
        </div>

        <button
          type="button"
          className="crux-nav-hamburger"
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="crux-nav-mobile"
          style={{
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: '#fff',
          }}
        >
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setMobileOpen(false)}
              className="crux-nav-link crux-nav-link--mobile"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}

      <NavStyles />
    </header>
  );
}

function NavStyles() {
  return (
    <style>{`
      .crux-nav-center {
        display: flex;
        gap: 28px;
        margin-left: 32px;
      }
      .crux-nav-link {
        color: ${colors.textSecondary};
        font-size: 14px;
        font-weight: 500;
        text-decoration: none;
        transition: color 120ms ease;
      }
      .crux-nav-link:hover { color: ${colors.textPrimary}; }
      .crux-nav-cta {
        margin-left: auto;
        display: flex;
        gap: 10px;
        align-items: center;
      }
      .crux-nav-hamburger {
        display: none;
        margin-left: auto;
        width: 40px;
        height: 40px;
        background: transparent;
        border: 1px solid ${colors.border};
        border-radius: 10px;
        cursor: pointer;
        padding: 0;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 4px;
      }
      .crux-nav-hamburger span {
        width: 18px;
        height: 1.5px;
        background: ${colors.textPrimary};
        border-radius: 2px;
      }
      .crux-nav-mobile {
        display: none;
        flex-direction: column;
        padding: 8px 24px 16px;
      }
      .crux-nav-link--mobile {
        padding: 10px 0;
        font-size: 15px;
      }
      @media (max-width: 900px) {
        .crux-nav-center { display: none; }
        .crux-nav-cta { margin-left: auto; gap: 6px; }
      }
      @media (max-width: 640px) {
        .crux-nav-cta .crux-btn--ghost { display: none; }
        .crux-nav-hamburger { display: flex; }
        .crux-nav-mobile { display: flex; }
      }

      /* Buttons */
      .crux-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 10px 18px;
        min-height: 40px;
        border-radius: 999px;
        font-size: 14px;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
        font-family: ${fontStack};
        line-height: 1;
        border: 1px solid transparent;
        transition: background-color 120ms ease, transform 120ms ease, box-shadow 120ms ease;
      }
      .crux-btn--primary {
        background: ${colors.primary};
        color: #fff;
        box-shadow: 0 4px 12px rgba(47, 91, 255, 0.18);
      }
      .crux-btn--primary:hover {
        background: #2347D9;
      }
      .crux-btn--ghost {
        background: transparent;
        color: ${colors.primary};
        border-color: ${colors.primary};
      }
      .crux-btn--ghost:hover {
        background: rgba(47, 91, 255, 0.06);
      }
      .crux-btn--big {
        padding: 14px 26px;
        min-height: 48px;
        font-size: 15px;
      }
    `}</style>
  );
}

function SmoothScrollGlobal() {
  return (
    <style>{`
      html { scroll-behavior: smooth; scroll-padding-top: 80px; }
      @media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }
    `}</style>
  );
}

function CruxHexImg({ size = 32 }) {
  return (
    <img
      src="/assets/crux-hex.png"
      alt="Curx AI"
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
}

/* ================================================================
   Hero
   ================================================================ */

function Hero({ onWatchDemo }) {
  return (
    <section
      style={{
        position: 'relative',
        padding: '64px 24px 80px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.05fr 1fr',
          gap: 56,
          alignItems: 'center',
        }}
        className="crux-hero-grid"
      >
        <div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              backgroundColor: 'rgba(47, 91, 255, 0.08)',
              color: colors.primary,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            <SparkleGlyph color={colors.primary} /> Your Personal Decision Engine
          </span>

          <h1
            className="crux-h1"
            style={{
              margin: '20px 0 0',
              color: colors.textPrimary,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
            }}
          >
            Clarity before <span style={{ color: colors.primary }}>action.</span>
          </h1>

          <p
            style={{
              margin: '20px 0 0',
              fontSize: 18,
              lineHeight: 1.6,
              color: colors.textSecondary,
              maxWidth: 540,
            }}
          >
            Curx AI analyzes your energy, growth, and life patterns to tell you
            what truly matters and what to do next.
          </p>

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 28,
              flexWrap: 'wrap',
            }}
          >
            <a href="/login" className="crux-btn crux-btn--primary crux-btn--big">
              Start Your Journey <span aria-hidden="true">→</span>
            </a>
            <button
              type="button"
              onClick={onWatchDemo}
              className="crux-btn crux-btn--ghost crux-btn--big"
            >
              <PlayGlyph color={colors.primary} /> Watch Demo
            </button>
          </div>

          <FeaturePillsRow />
        </div>

        <div className="crux-hero-illustration" style={{ display: 'flex', justifyContent: 'center' }}>
          <OrbitalIllustration />
        </div>

        <style>{`
          .crux-h1 { font-size: 72px; }
          @media (max-width: 1024px) {
            .crux-hero-grid {
              grid-template-columns: 1fr !important;
              gap: 48px !important;
            }
          }
          @media (max-width: 640px) {
            .crux-h1 { font-size: 44px; }
          }
        `}</style>
      </div>
    </section>
  );
}

function FeaturePillsRow() {
  const pills = [
    { glyph: <BrainGlyph color={colors.primary} />, label: 'AI-Powered Insights', tint: 'rgba(47, 91, 255, 0.08)' },
    { glyph: <BoltGlyph color="#3FA37A" />, label: 'Energy Intelligence', tint: 'rgba(111, 207, 151, 0.14)' },
    { glyph: <TrendGlyph color="#7B66E8" />, label: 'Growth Guidance', tint: 'rgba(155, 138, 251, 0.14)' },
    { glyph: <HexGlyph color="#3AA0C7" />, label: 'Life System', tint: 'rgba(86, 204, 242, 0.16)' },
  ];
  return (
    <div
      style={{
        marginTop: 32,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 10,
        maxWidth: 540,
      }}
    >
      {pills.map((p) => (
        <div
          key={p.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            backgroundColor: p.tint,
            borderRadius: radius.md,
            fontSize: 13,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          <span style={{ display: 'inline-flex', flexShrink: 0 }}>{p.glyph}</span>
          {p.label}
        </div>
      ))}
    </div>
  );
}

/* ================================================================
   Orbital illustration
   ================================================================ */

function OrbitalIllustration() {
  // 480x480 viewBox; centered geometry; static (no animation by default).
  return (
    <div
      role="img"
      aria-label="A blue hexagon at the center with three module orbits — Energy, System, Growth — circling around it."
      style={{
        width: '100%',
        maxWidth: 480,
        aspectRatio: '1 / 1',
      }}
    >
      <svg viewBox="0 0 480 480" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="cruxHexGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(47, 91, 255, 0.22)" />
            <stop offset="60%" stopColor="rgba(47, 91, 255, 0.06)" />
            <stop offset="100%" stopColor="rgba(47, 91, 255, 0)" />
          </radialGradient>
          <linearGradient id="cruxHexFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4671FF" />
            <stop offset="100%" stopColor="#2F5BFF" />
          </linearGradient>
        </defs>

        {/* Soft glow under the hexagon */}
        <circle cx="240" cy="240" r="180" fill="url(#cruxHexGlow)" />

        {/* Three concentric thin orbital rings */}
        <ellipse cx="240" cy="240" rx="200" ry="74" fill="none" stroke="#E5E7EB" strokeWidth="1" />
        <ellipse
          cx="240"
          cy="240"
          rx="160"
          ry="160"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="1"
          strokeDasharray="2 4"
        />
        <ellipse cx="240" cy="240" rx="120" ry="200" fill="none" stroke="#E5E7EB" strokeWidth="1" />

        {/* Scattered dots along orbits */}
        <OrbitDots />

        {/* Center hexagon (blue, slightly outlined) */}
        <Hexagon cx={240} cy={240} r={66} fill="url(#cruxHexFill)" />
        <Hexagon cx={240} cy={240} r={66} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" inset />
        <text
          x="240"
          y="248"
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill="#fff"
          fontFamily={fontStack}
          letterSpacing="0.04em"
        >
          CURX
        </text>

        {/* Module pills — Energy (top), System (right), Growth (bottom-left) */}
        <ModulePill x={240} y={56} label="Energy" accent="#3FA37A" tint="rgba(111, 207, 151, 0.16)" glyph="heart" />
        <ModulePill x={400} y={210} label="System" accent="#3AA0C7" tint="rgba(86, 204, 242, 0.16)" glyph="grid" />
        <ModulePill x={104} y={362} label="Growth" accent="#7B66E8" tint="rgba(155, 138, 251, 0.16)" glyph="star" />
      </svg>
    </div>
  );
}

function Hexagon({ cx, cy, r, fill, stroke, strokeWidth, inset }) {
  // Pointy-top hex vertices.
  const rr = inset ? r - 6 : r;
  const points = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    points.push(`${cx + rr * Math.cos(angle)},${cy + rr * Math.sin(angle)}`);
  }
  return (
    <polygon
      points={points.join(' ')}
      fill={fill || 'none'}
      stroke={stroke}
      strokeWidth={strokeWidth || 0}
      strokeLinejoin="round"
    />
  );
}

function OrbitDots() {
  // A handful of small dots scattered along visual orbital paths.
  const pts = [
    [80, 244, 1.6],
    [120, 196, 1.4],
    [192, 78, 1.8],
    [344, 90, 1.4],
    [402, 162, 1.6],
    [410, 308, 1.6],
    [340, 396, 1.4],
    [200, 412, 1.8],
    [76, 318, 1.6],
    [60, 240, 1.2],
  ];
  return (
    <g fill="#C7CEDB">
      {pts.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} />
      ))}
    </g>
  );
}

function ModulePill({ x, y, label, accent, tint, glyph }) {
  // Group with rect background, glyph and label.
  const W = 116;
  const H = 36;
  return (
    <g transform={`translate(${x - W / 2}, ${y - H / 2})`}>
      <rect
        x="0"
        y="0"
        width={W}
        height={H}
        rx={H / 2}
        ry={H / 2}
        fill="#fff"
        stroke={tint}
        strokeWidth="1"
        style={{ filter: 'drop-shadow(0 2px 8px rgba(17, 24, 39, 0.06))' }}
      />
      <circle cx={H / 2} cy={H / 2} r="11" fill={tint} />
      <g transform={`translate(${H / 2 - 7}, ${H / 2 - 7})`} stroke={accent} strokeWidth="1.6" fill="none"
         strokeLinecap="round" strokeLinejoin="round">
        {glyph === 'heart' && (
          <>
            <path d="M7 12.6S2 9.7 2 6.2A3.2 3.2 0 0 1 7 4a3.2 3.2 0 0 1 5 2.2c0 3.5-5 6.4-5 6.4z" />
          </>
        )}
        {glyph === 'grid' && (
          <>
            <rect x="2" y="2" width="4.5" height="4.5" />
            <rect x="7.5" y="2" width="4.5" height="4.5" />
            <rect x="2" y="7.5" width="4.5" height="4.5" />
            <rect x="7.5" y="7.5" width="4.5" height="4.5" />
          </>
        )}
        {glyph === 'star' && (
          <>
            <path d="M7 1.6l1.7 3.6 4 .5-3 2.7.8 4-3.5-2-3.5 2 .8-4-3-2.7 4-.5z" />
          </>
        )}
      </g>
      <text
        x={H + 6}
        y={H / 2 + 4}
        fontSize="13"
        fontWeight="600"
        fill={colors.textPrimary}
        fontFamily={fontStack}
      >
        {label}
      </text>
    </g>
  );
}

/* ================================================================
   Social proof band
   ================================================================ */

/**
 * TrustSection — calm, minimal credibility moment between hero and
 * Philosophy. Headline + subtext + a quiet row of tool wordmarks,
 * positioning Curx as a decision layer that sits alongside what
 * people already use. No logos available (no simple-icons /
 * react-icons in package.json), so wordmarks per spec.
 */
function TrustSection() {
  const tools = ['Google', 'Microsoft', 'Notion', 'Amazon', 'Figma', 'Stripe'];
  return (
    <section
      style={{
        backgroundColor: 'var(--bg-surface-2)',
      }}
    >
      <div
        className="crux-trust"
        style={{
          maxWidth: 1040,
          margin: '0 auto',
          padding: '96px 24px',
          textAlign: 'center',
        }}
      >
        <h2
          className="crux-trust__h"
          style={{
            margin: 0,
            color: colors.textPrimary,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
          }}
        >
          Used by people figuring out what matters.
        </h2>
        <p
          style={{
            margin: '16px auto 0',
            maxWidth: 640,
            color: colors.textSecondary,
            fontSize: 16,
            lineHeight: 1.6,
            fontWeight: 400,
          }}
        >
          Curx fits alongside the tools you already use — not replacing them,
          but helping you decide what to focus on.
        </p>

        <ul
          className="crux-trust__row"
          aria-label="Tools Curx fits alongside"
          style={{
            listStyle: 'none',
            margin: '48px 0 0',
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 48,
          }}
        >
          {tools.map((t) => (
            <li key={t}>
              <ToolWordmark>{t}</ToolWordmark>
            </li>
          ))}
        </ul>

        <p
          style={{
            margin: '32px 0 0',
            fontSize: 13,
            color: colors.textSecondary,
            fontStyle: 'italic',
          }}
        >
          Not a dashboard. A decision layer.
        </p>
      </div>
      <style>{`
        .crux-trust__h {
          font-size: 28px;
        }
        @media (max-width: 640px) {
          .crux-trust {
            padding: 64px 20px !important;
          }
          .crux-trust__h {
            font-size: 22px;
          }
          .crux-trust__row {
            gap: 28px !important;
          }
        }
      `}</style>
    </section>
  );
}

/**
 * ToolWordmark — text-only fallback when no logo library is available.
 * Per Phase 5.3 spec: 16px / weight 500 / #9CA3AF / 0.05em letter-spacing.
 * Default opacity 0.5, hover 0.8. Grayscale filter applied as a no-op
 * for symmetry with the future logo-image swap.
 */
function ToolWordmark({ children }) {
  return (
    <span
      style={{
        display: 'inline-block',
        height: 28,
        lineHeight: '28px',
        fontSize: 16,
        fontWeight: 500,
        letterSpacing: '0.05em',
        color: '#9CA3AF',
        opacity: 0.5,
        filter: 'grayscale(1)',
        transition: 'opacity 160ms ease',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.8)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
    >
      {children}
    </span>
  );
}

/* ================================================================
   Philosophy
   ================================================================ */

function Philosophy() {
  return (
    <Section id="philosophy" eyebrow="Manifesto" title="Why Curx exists" align="left">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          gap: 56,
          alignItems: 'center',
        }}
        className="crux-philosophy-grid"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={prose}>
            Modern life produces more data about you than ever — calendars, sleep
            trackers, finances, learning apps, to-do lists. None of it tells you
            what to actually do.
          </p>
          <p style={prose}>
            <strong style={{ color: colors.textPrimary }}>Curx is a decision engine, not a dashboard.</strong>{' '}
            It reads your life signals across energy, growth, and daily systems,
            and produces one clear answer: what matters now, and what to do next.
          </p>
          <p style={prose}>
            Calm. Direct. Practical. Built for people who want to act, not analyze.
          </p>
        </div>
        <PhilosophyVisual />
        <style>{`
          @media (max-width: 1024px) {
            .crux-philosophy-grid {
              grid-template-columns: 1fr !important;
              gap: 32px !important;
            }
          }
        `}</style>
      </div>
    </Section>
  );
}

function PhilosophyVisual() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg viewBox="0 0 360 220" width="100%" style={{ maxWidth: 360 }} xmlns="http://www.w3.org/2000/svg">
        {/* Question hex (left) */}
        <Hexagon cx={70} cy={110} r={48} fill="#fff" stroke={colors.border} strokeWidth={1.5} />
        <text x="70" y="120" textAnchor="middle" fontSize="34" fontWeight="700" fill={colors.textSecondary} fontFamily={fontStack}>?</text>
        {/* Arrow */}
        <line x1="138" y1="110" x2="220" y2="110" stroke={colors.primary} strokeWidth="1.5" strokeDasharray="4 4" />
        <path d="M218 104 L228 110 L218 116 Z" fill={colors.primary} />
        {/* Answer hex (right) */}
        <Hexagon cx={290} cy={110} r={48} fill="url(#philFill)" />
        <text x="290" y="121" textAnchor="middle" fontSize="32" fontWeight="700" fill="#fff" fontFamily={fontStack}>✓</text>
        <defs>
          <linearGradient id="philFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4671FF" />
            <stop offset="100%" stopColor="#2F5BFF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

/* ================================================================
   How it works
   ================================================================ */

function HowItWorks() {
  const steps = [
    { n: 1, title: 'Connect', body: 'Curx reads signals from your energy, skills, and daily systems.' },
    { n: 2, title: 'Analyze', body: 'An always-on decision engine processes patterns in real time.' },
    { n: 3, title: 'Decide',  body: 'One clear answer: state, action, reason.' },
    { n: 4, title: 'Act',     body: 'You move. Curx learns and adapts.' },
  ];
  return (
    <Section id="how-it-works" eyebrow="The Loop" title="How Curx works">
      <div className="crux-how-grid">
        {steps.map((s, i) => (
          <div key={s.n} style={{ position: 'relative' }}>
            {i < steps.length - 1 && <ConnectorLine />}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: '28px 24px',
                backgroundColor: '#fff',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.lg,
                position: 'relative',
                zIndex: 1,
                height: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: colors.primary,
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                  boxShadow: '0 4px 12px rgba(47, 91, 255, 0.22)',
                }}
              >
                {s.n}
              </div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.textPrimary }}>{s.title}</h3>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: colors.textSecondary }}>{s.body}</p>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        .crux-how-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          position: relative;
        }
        @media (max-width: 1024px) {
          .crux-how-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .crux-how-grid { grid-template-columns: 1fr; gap: 16px; }
        }
      `}</style>
    </Section>
  );
}

function ConnectorLine() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 50,
        right: -12,
        width: 24,
        height: 1,
        background: colors.border,
        zIndex: 0,
      }}
      className="crux-connector"
    />
  );
}

/* ================================================================
   Features
   ================================================================ */

function Features() {
  const cards = [
    {
      label: 'Energy',
      accent: 'var(--success)',
      tint: 'rgba(111, 207, 151, 0.14)',
      glyph: <BoltGlyph color="#3FA37A" />,
      body: 'Track mental, physical, and stress levels. Curx watches for burnout signals before they hit.',
    },
    {
      label: 'Growth',
      accent: '#7B66E8',
      tint: 'rgba(155, 138, 251, 0.14)',
      glyph: <SparkleGlyph color="#7B66E8" />,
      body: 'Sharpen skills with AI-guided interview practice. See your trajectory clearly, level up faster.',
    },
    {
      label: 'System',
      accent: '#3AA0C7',
      tint: 'rgba(86, 204, 242, 0.16)',
      glyph: <GridGlyph color="#3AA0C7" />,
      body: 'Money, health, and planner — your daily life organized. Optimization recommendations baked in.',
    },
  ];

  return (
    <Section id="features" eyebrow="The Modules" title="Three modules, one decision engine">
      <div className="crux-features-grid">
        {cards.map((c) => (
          <div
            key={c.label}
            style={{
              backgroundColor: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.lg,
              padding: '28px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0, left: 0, bottom: 0, width: 4,
                backgroundColor: c.accent,
              }}
            />
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: c.tint,
                color: c.accent,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {c.glyph}
            </span>
            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: colors.textPrimary, letterSpacing: '-0.01em' }}>
              {c.label}
            </h3>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: colors.textSecondary }}>
              {c.body}
            </p>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 24,
          backgroundColor: '#fff',
          border: `1px solid ${colors.border}`,
          borderRadius: radius.lg,
          padding: '24px 28px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0, left: 0, bottom: 0, width: 4,
            backgroundColor: colors.primary,
          }}
        />
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: 'rgba(47, 91, 255, 0.10)',
            color: colors.primary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlusGlyph color={colors.primary} />
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: colors.textPrimary }}>
            Copilot — your personal decision engine
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: colors.textSecondary, lineHeight: 1.6 }}>
            Synthesizes signals from all three modules into a single, calm decision.
            Ask anything; get one clear answer.
          </p>
        </div>
      </div>

      <style>{`
        .crux-features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        @media (max-width: 1024px) {
          .crux-features-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .crux-features-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </Section>
  );
}

/* ================================================================
   About
   ================================================================ */

function About() {
  // Body uses regular weight by default (prose style); only the founders'
  // names are semi-bold per spec. "(Founder)" and "(Co-Founder)" stay regular.
  const bodyParagraph = {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.7,
    color: colors.textSecondary,
  };

  return (
    <Section id="about" eyebrow="The Team" title="Why Curx exists" align="left" maxWidth={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <p style={bodyParagraph}>
          We built Curx because we needed it.
        </p>
        <p style={bodyParagraph}>
          Modern professional life produces too much data — calendars, sleep
          trackers, finances, learning apps, to-do lists — and almost no clarity.
          Existing tools track. Curx decides.
        </p>
        <p style={bodyParagraph}>
          Curx is a decision engine, not a dashboard. It reads your life signals
          across energy, growth, and daily systems, and produces one clear answer:
          what matters now, and what to do next.
        </p>
        <p style={bodyParagraph}>
          Built with intention by{' '}
          <span style={{ fontWeight: 600, color: colors.textPrimary }}>Lakshmi Anne</span>{' '}
          (Founder) and{' '}
          <span style={{ fontWeight: 600, color: colors.textPrimary }}>Harsha Gaddam</span>{' '}
          (Co-Founder). Currently in private beta.
        </p>
      </div>
    </Section>
  );
}

/* ================================================================
   Final CTA — waitlist email
   ================================================================ */

function FinalCTA() {
  const [email, setEmail] = useState('');
  return (
    <section
      id="waitlist"
      style={{
        background: 'linear-gradient(180deg, rgba(47, 91, 255, 0.05) 0%, rgba(47, 91, 255, 0.10) 100%)',
        padding: '88px 24px',
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <h2
          className="crux-h2"
          style={{
            margin: 0,
            color: colors.textPrimary,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          Stop drifting. <span style={{ color: colors.primary }}>Start deciding.</span>
        </h2>
        <p
          style={{
            margin: '14px 0 28px',
            fontSize: 17,
            lineHeight: 1.6,
            color: colors.textSecondary,
            maxWidth: 540,
          }}
        >
          Join the waitlist for early access.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = email.trim();
            const target = trimmed
              ? `/waitlist?email=${encodeURIComponent(trimmed)}`
              : '/waitlist';
            window.location.href = target;
          }}
          style={{
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            style={{
              flex: '1 1 240px',
              padding: '14px 18px',
              minHeight: 48,
              border: `1px solid ${colors.border}`,
              borderRadius: 999,
              fontSize: 15,
              fontFamily: fontStack,
              color: colors.textPrimary,
              backgroundColor: '#fff',
              outline: 'none',
            }}
            onFocus={(e) => (e.target.style.borderColor = colors.primary)}
            onBlur={(e) => (e.target.style.borderColor = colors.border)}
          />
          <button type="submit" className="crux-btn crux-btn--primary crux-btn--big" style={{ flex: '0 0 auto' }}>
            Join the waitlist
          </button>
        </form>

        <p style={{ margin: '14px 0 0', fontSize: 12, color: colors.textSecondary }}>
          We'll never sell your email. One quick question on the next page, then you're done.
        </p>
      </div>

      <style>{`
        .crux-h2 { font-size: 48px; }
        @media (max-width: 640px) { .crux-h2 { font-size: 32px; } }
      `}</style>
    </section>
  );
}

/* ================================================================
   Footer
   ================================================================ */

function Footer() {
  return (
    <footer
      style={{
        backgroundColor: '#fff',
        borderTop: `1px solid ${colors.border}`,
        padding: '48px 24px',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 1fr',
          gap: 32,
          alignItems: 'flex-start',
        }}
        className="crux-footer-grid"
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <CruxHexImg size={28} />
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.02em' }}>CURX AI</span>
          </div>
          <div style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
            © {new Date().getFullYear()} CWORLD GROUP LIMITED.<br />
            Built with intention.
          </div>
        </div>

        <FooterCol title="Explore">
          <a href="#philosophy" className="crux-footer-link">Philosophy</a>
          <a href="#how-it-works" className="crux-footer-link">How It Works</a>
          <a href="#features" className="crux-footer-link">Features</a>
          <a href="#about" className="crux-footer-link">About</a>
          <a href="/login" className="crux-footer-link">Sign in</a>
        </FooterCol>

        <FooterCol title="Contact">
          <a href="mailto:admin@cworldgroup.co.uk" className="crux-footer-link">
            admin@cworldgroup.co.uk
          </a>
        </FooterCol>
      </div>

      <style>{`
        .crux-footer-link {
          display: block;
          padding: 4px 0;
          color: ${colors.textSecondary};
          font-size: 13px;
          text-decoration: none;
          transition: color 120ms ease;
        }
        .crux-footer-link:hover { color: ${colors.textPrimary}; }
        @media (max-width: 768px) {
          .crux-footer-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
      `}</style>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

/* ================================================================
   Demo modal
   ================================================================ */

function DemoModal({ onClose }) {
  const cardRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Demo coming soon"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
    >
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          backgroundColor: '#fff',
          borderRadius: radius.lg,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          padding: spacing.lg,
          fontFamily: fontStack,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: 'rgba(47, 91, 255, 0.10)',
            color: colors.primary,
            margin: '0 auto 6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayGlyph color={colors.primary} />
        </div>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.textPrimary }}>
          Demo coming soon
        </h3>
        <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary, lineHeight: 1.55 }}>
          Join the waitlist to be the first to see it.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose} className="crux-btn crux-btn--ghost">Close</button>
          <a
            href="#waitlist"
            onClick={onClose}
            className="crux-btn crux-btn--primary"
          >
            Join waitlist
          </a>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   Section + prose helpers
   ================================================================ */

function Section({ id, eyebrow, title, children, align = 'center', maxWidth = 1200 }) {
  return (
    <section
      id={id}
      style={{
        padding: '88px 24px',
        backgroundColor: 'transparent',
      }}
    >
      <div style={{ maxWidth, margin: '0 auto' }}>
        <div
          style={{
            textAlign: align,
            marginBottom: 40,
            ...(align === 'left' ? { paddingRight: 0 } : { paddingLeft: 0 }),
          }}
        >
          {eyebrow && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: colors.primary,
                marginBottom: 10,
              }}
            >
              {eyebrow}
            </div>
          )}
          <h2
            className="crux-section-h2"
            style={{
              margin: 0,
              color: colors.textPrimary,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {title}
          </h2>
        </div>
        {children}
      </div>
      <style>{`
        .crux-section-h2 { font-size: 44px; }
        @media (max-width: 640px) { .crux-section-h2 { font-size: 30px; } }
      `}</style>
    </section>
  );
}

const prose = {
  margin: 0,
  fontSize: 17,
  lineHeight: 1.7,
  color: colors.textSecondary,
};

/* ================================================================
   Glyphs
   ================================================================ */

function SparkleGlyph({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
    </svg>
  );
}
function PlayGlyph({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
function BrainGlyph({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 3a3 3 0 0 0-3 3v1a3 3 0 0 0-3 3 3 3 0 0 0 1.5 2.6A3 3 0 0 0 6 17a3 3 0 0 0 3 3V3z" />
      <path d="M15 3a3 3 0 0 1 3 3v1a3 3 0 0 1 3 3 3 3 0 0 1-1.5 2.6A3 3 0 0 1 18 17a3 3 0 0 1-3 3V3z" />
    </svg>
  );
}
function BoltGlyph({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" />
    </svg>
  );
}
function TrendGlyph({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
    </svg>
  );
}
function HexGlyph({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 21 7 21 17 12 22 3 17 3 7" />
    </svg>
  );
}
function GridGlyph({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}
function PlusGlyph({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}
